import { Link, useParams } from "react-router-dom";
import "./PartnersDetailsPage.scss";
import { usePartnerDetailViewModel } from "../viewmodel/usePartnerDetailViewModel";
import Loader from "@/core/components/loader/Loader";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { useState } from "react";
import { Button } from "primereact/button";
import CallCard from "@/features/calls/components/CallCard/CallCard";
import { adaptDeclaredInterestCallToCall } from "../utils/callAdapter";
import { MemberCard } from "../components/MemberCard";
import { formatFileSize, getFileTypeFromMimeType } from "@/core/utils/format";
import { imageList } from "@/core/utils/imageList";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { useUnit } from "effector-react";
import { $userBasicInfo } from "@/features/user/store";
import { Idea } from "@/features/ideas/model/types";
import { IdeaGridItem } from "@/features/ideas/components/IdeaGridItem";
import { $sectorsTerms } from "@/core/store/terms";
import { Share } from "@/core/components/share/Share";
import { useNavigate } from "react-router-dom";
import { IdeaListItem } from "@/features/ideas/components/IdeaListItem";
import { SEO } from "@/core/components/SEO/SEO";

export const PartnersDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [showMore, setShowMore] = useState(false);
  const [showAllSectors, setShowAllSectors] = useState(false);
  const [currentCallIndex, setCurrentCallIndex] = useState(0);
  const [currentIdeaIndex, setCurrentIdeaIndex] = useState(0);
  const { partner, loading, error, toggleMemberFollow } =
    usePartnerDetailViewModel(id);
  const userBasic = useUnit($userBasicInfo);
  const sectorsTerms = useUnit($sectorsTerms);
  const navigate = useNavigate();

  const handleMemberFollow = (memberId: string, isFollowing: boolean) => {
    toggleMemberFollow(memberId, isFollowing);
  };

  const handleEditOrganization = () => {
    navigate("/settings/organization");
  };

  // Carousel navigation for declared interest calls
  const callsPerSlide = 3;
  const declaredCalls = partner?.declared_interest_calls?.data || [];
  const totalCallSlides = Math.ceil(declaredCalls.length / callsPerSlide);

  const handlePreviousCall = () => {
    setCurrentCallIndex((prev) => (prev > 0 ? prev - 1 : totalCallSlides - 1));
  };

  const handleNextCall = () => {
    setCurrentCallIndex((prev) => (prev < totalCallSlides - 1 ? prev + 1 : 0));
  };

  // Carousel navigation for ideas
  const ideasPerSlide = 3;
  const ideas = partner?.ideas?.data || [];
  const totalIdeaSlides = Math.ceil(ideas.length / ideasPerSlide);

  const handlePreviousIdea = () => {
    setCurrentIdeaIndex((prev) => (prev > 0 ? prev - 1 : totalIdeaSlides - 1));
  };

  const handleNextIdea = () => {
    setCurrentIdeaIndex((prev) => (prev < totalIdeaSlides - 1 ? prev + 1 : 0));
  };

  const checkIfMember = (): boolean => {
    // Check if the user's partner_id exists in the partner's members
    // We need to check if any member belongs to the same partner organization
    const partnerID =
      userBasic?.partner &&
      typeof userBasic.partner === "object" &&
      userBasic.partner?.partner_id
        ? userBasic.partner?.partner_id
        : -1;
    return partnerID === id;
  };

  // Helper function to map sector IDs to sector names
  const getSectorNameById = (sectorId: string): string => {
    if (!sectorsTerms.data || sectorsTerms.data.length === 0) {
      return sectorId; // fallback to ID if terms not loaded
    }
    const sector = sectorsTerms.data.find((term) => term.tid === sectorId);
    return sector ? sector.name : sectorId;
  };

  if (loading) {
    return (
      <div className="partner-detail-page">
        <div className="pageTitle">Partner Details</div>
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="partner-detail-page">
        <div className="pageTitle">Partner Details</div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="partner-detail-page">
        <div className="pageTitle">Partner Details</div>
        <div className="error-message">Partner not found</div>
      </div>
    );
  }

  return (
    <>
      <div className="container user-detail-header">
        <SEO
          title={`${partner ? partner.title : "Partners"} | EUcalls`}
          description={`${partner ? partner.title : "Partners"}`}
        />

        <div className="back-button" onClick={() => navigate(-1)}>
          <SvgIcon icon="icon-arrow-left" />
          <span>Back</span>
        </div>
        {userBasic?.partner &&
          typeof userBasic.partner === "object" &&
          userBasic.partner.status === "admin" &&
          userBasic.partner.partner_id === id && (
            <div className="edit-organization-section">
              <Link to={"/settings/organization/info"} className="editProfile">
                <SvgIcon icon="icon-editProfile" /> Edit Organization Info
              </Link>
            </div>
          )}
      </div>
      <div className="partner-detail-page">
        <div className="partner-container">
          <div className="partner-header">
            <div className="header-left">
              <div className="partner-logo">
                {partner.logo_uri &&
                partner.logo_uri !== "partner-images/logo_not_available.jpg" ? (
                  <img src={partner.logo_uri} alt={partner.title} />
                ) : (
                  <img src={imageList.noOrgimage} alt={partner.title} />
                )}
              </div>
              <div className="partner-info">
                <h1>{partner.title}</h1>
                <div className="partner-meta">
                  {partner.partner_type.name && (
                    <span className="meta-item">
                      <SvgIcon icon="icon-building" />
                      {partner.partner_type?.name}
                    </span>
                  )}
                  <span className="meta-item">
                    <SvgIcon icon="icon-location" />
                    {partner.country?.name}
                  </span>
                  {partner.since && (
                    <span className="meta-item">
                      <SvgIcon icon="icon-clock" />
                      {partner.since}
                    </span>
                  )}
                  {partner.size.name && partner.size.name.length > 1 && (
                    <span className="meta-item">
                      <SvgIcon icon="icon-users" />
                      {partner.size?.name}
                    </span>
                  )}
                  {partner.pic_number && (
                    <span className="meta-item">
                      <SvgIcon icon="icon-hash" />
                      {partner.pic_number && `PIC Number ${partner.pic_number}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="header-right">
              {partner.linkedin !== "" && (
                <a
                  href={`${partner.linkedin}`}
                  target="_blank"
                  className={`social-btn ${
                    partner.linkedin === "" ? "noUrl" : ""
                  }`}
                >
                  <SvgIcon icon="icon-linkedIn" />
                </a>
              )}
              {partner.facebook !== "" && (
                <a
                  href={`${partner.facebook}`}
                  target="_blank"
                  className={`social-btn ${
                    partner.facebook === "" ? "noUrl" : ""
                  }`}
                >
                  <SvgIcon icon="icon-fb" />
                </a>
              )}
              {partner.twitter !== "" && (
                <a
                  href={`${partner.twitter}`}
                  target="_blank"
                  className={`social-btn ${
                    partner.twitter === "" ? "noUrl" : ""
                  }`}
                >
                  <SvgIcon icon="icon-x" />
                </a>
              )}
              {partner.website_url !== "" && (
                <a
                  href={`${partner.website_url}`}
                  target="_blank"
                  className={`social-btn ${
                    partner.website_url === "" ? "noUrl" : ""
                  }`}
                >
                  <SvgIcon icon="icon-link" />
                </a>
              )}
              <Share />
            </div>
          </div>

          {partner.sectors && partner.sectors.length > 0 && (
            <div className="partner-sectors">
              {partner.sectors
                .slice(0, showAllSectors ? partner.sectors.length : 6)
                .map((sector, index) => (
                  <span key={index} className="sector-tag">
                    {sector.name}
                  </span>
                ))}
              {partner.sectors.length > 6 && (
                <span
                  className="sector-tag more"
                  onClick={() => setShowAllSectors(!showAllSectors)}
                  style={{ cursor: "pointer" }}
                >
                  {showAllSectors
                    ? "Show less"
                    : `+${partner.sectors.length - 6}`}
                </span>
              )}
            </div>
          )}

          {!(
            typeof userBasic?.partner === "object" &&
            userBasic?.partner?.partner_id === id &&
            userBasic?.partner?.status === "member"
          ) && (
            <div className="partner-body">
              <div className="content-left">
                <section className="description-section">
                  <h2>Description</h2>
                  <div
                    className={`description-content ${
                      !showMore ? "truncated" : ""
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: partner.description && partner.description,
                    }}
                  />
                </section>
              </div>

              {partner.has_experience && (
                <div className="content-right">
                  <h2>EU projects</h2>
                  <section className="eu-projects-section">
                    <div className="project-stats">
                      <div className="stat">
                        <span className="stat-label">Implemented</span>
                        <span className="stat-value">
                          {partner.implemented_projects || 0}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Leader</span>
                        <span className="stat-value">
                          {partner.project_leader || 0}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Partner</span>
                        <span className="stat-value">
                          {partner.project_partner || 0}
                        </span>
                      </div>
                    </div>
                    {partner.programmes && partner.programmes.length > 0 && (
                      <div className="programmes">
                        <h3>Programmes</h3>
                        <p>
                          {partner.programmes.map((p) => p.name).join(", ")}
                        </p>
                      </div>
                    )}
                  </section>

                  {partner.documents && partner.documents.length > 0 && (
                    <section className="documents-section">
                      <h2>Documents</h2>
                      <div className="document-list">
                        {partner.documents.map((doc, index) => (
                          <a
                            href={doc.url}
                            target="_blank"
                            key={index}
                            className="document-item"
                          >
                            <div className="doc-icon">
                              <BgImage img={imageList.file} />
                              <span>
                                {getFileTypeFromMimeType(doc.mime_type)}
                              </span>
                            </div>
                            <div className="doc-info">
                              <span className="doc-name">
                                {doc.description}
                              </span>
                              {doc.filesize && (
                                <span className="doc-filesize">
                                  {formatFileSize(doc.filesize)}
                                </span>
                              )}
                            </div>
                          </a>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {partner.members && partner.members.length > 0 && (
          <section className="members-section">
            <h2>Members ({partner.members.length})</h2>
            <div className="members-grid">
              {partner.members.map((member, index) => (
                <MemberCard
                  showAdminTag={checkIfMember()}
                  key={index}
                  type="user"
                  member={member}
                  partnerId={partner.id}
                  onMemberFollow={handleMemberFollow}
                />
              ))}
            </div>
          </section>
        )}
        {partner.declared_interest_calls &&
          partner.declared_interest_calls.data &&
          partner.declared_interest_calls.data.length > 0 && (
            <section className="declared-calls-section">
              <div className="declared-calls-header">
                <h2>
                  Declared interest in calls (
                  {partner.declared_interest_calls.count})
                </h2>
                <div className="navigation-arrows">
                  <Button
                    icon="pi pi-chevron-left"
                    text
                    className="nav-arrow"
                    onClick={handlePreviousCall}
                    disabled={totalCallSlides <= 1}
                  />
                  <Button
                    icon="pi pi-chevron-right"
                    text
                    className="nav-arrow"
                    onClick={handleNextCall}
                    disabled={totalCallSlides <= 1}
                  />
                </div>
              </div>

              <div className="declared-calls-carousel">
                <div
                  className="declared-calls-track"
                  style={{
                    transform: `translateX(-${currentCallIndex * 100}%)`,
                  }}
                >
                  {declaredCalls.map((call) => (
                    <div key={call.id} className="declared-call-item">
                      <CallCard call={adaptDeclaredInterestCallToCall(call)} />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        {partner.ideas &&
          partner.ideas.data &&
          partner.ideas.data.length > 0 && (
            <section className="declared-calls-section">
              <div className="declared-calls-header">
                <h2>Ideas ({partner.ideas.count})</h2>
                <div className="navigation-arrows">
                  <Button
                    icon="pi pi-chevron-left"
                    text
                    className="nav-arrow"
                    onClick={handlePreviousIdea}
                    disabled={totalIdeaSlides <= 1}
                  />
                  <Button
                    icon="pi pi-chevron-right"
                    text
                    className="nav-arrow"
                    onClick={handleNextIdea}
                    disabled={totalIdeaSlides <= 1}
                  />
                </div>
              </div>

              <div className="declared-calls-carousel">
                <div
                  className="declared-calls-track"
                  style={{
                    transform: `translateX(-${currentIdeaIndex * 100}%)`,
                  }}
                >
                  {ideas.map((item) => {
                    // Keep original data structure with sectors as string[]
                    const partnerIdea: any = item;

                    // Transform to Idea format with sectors as Sector objects
                    const transformedIdea = {
                      ...(partnerIdea as unknown as Idea),
                      sectors:
                        (partnerIdea.sectors as string[])?.map((item: any) => ({
                          id: item.id,
                          name: item.name,
                        })) || [],
                    };

                    return (
                      <div key={partnerIdea.id} className="declared-call-item">
                        <IdeaGridItem idea={transformedIdea} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
      </div>
    </>
  );
};
